// ----------------------------------------------------------------------
// <copyright file="Izenda.EmailActions.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

!(function() {
  var PropTypes = require('react').PropTypes;
  var compose = require('recompose').compose;
  var mapProps = require('recompose').mapProps;
  var withHandlers = require('recompose').withHandlers;
  var withState = require('recompose').withState;
  var setPropTypes = require('recompose').setPropTypes;
  var constants = require('logic/data/Izenda.EmailConstants');
  var fetcher = require('fetchers/Izenda.SystemSettingFetcher');
  var reportsDataFetcher = require('fetchers/Izenda.ReportsDataFetcher');
  var dashboardDataFetcher = require('fetchers/Izenda.DashboardDataFetcher');
  var uiState = require('logic/Izenda.UiState');
  var DEFAULT_SUBJECT = constants.EMAIL_SUBJECT.DEFAULT;
  var MESSAGE = constants.MESSAGE;

  const { assign, curry, isBoolean, isEmpty, split, pluck, uniq, chain, forEach } = require('lodash/fp');

  var Email = require('logic/models/Izenda.Email');
  var localizer = require('share/localization/Izenda.Localizer');

  var emailActions = compose(
    mapProps(function(props) {
      var email = new Email();
      email.setDataByKey('type', props.type);
      email.setDataByKey('subject', DEFAULT_SUBJECT[props.type]);
      return assign(props, { email: email });
    }),
    withState('loadingPromise', 'setLoadingPromise', null),
    withHandlers({
      onClosing: curry(onClosing),
      setToAddress: curry(setToAddress),
      setCcAddress: curry(setCcAddress),
      setSubject: curry(setSubject),
      setDeliveryMethod: curry(setDeliveryMethod),
      setAttachment: curry(setAttachment),
      setEmailBody: curry(setEmailBody),
      addNewEmail: curry(addNewEmail),
      addToEmailFromPopup: curry(addToEmailFromPopup),
      addCcEmailFromPopup: curry(addCcEmailFromPopup),
      getEmailTemplates: curry(getEmailTemplates)
    }),
    setPropTypes({
      onClosed: PropTypes.func,
      deliveryMethod: PropTypes.string
    })
  );

  /**
   * Closing Popup
   */
  function onClosing(props, cb, type) {
    var valManager = props.validation;
    if (type === 'accept') {
      fetcher.isHavemailConfiguration(uiState.tenantId).then(function(result) {
        if (result.success) {
          return valManager.validate().then(function(success) {
            if (success && validateEmailFormat(props)) {
              valManager.reset();
              var onclosedPromise = props.onClosed(props.email.buildForSend());
              onclosedPromise.then(function(onClosedResult) {
                var msg;
                if (isBoolean(onClosedResult)) {
                  msg = onClosedResult ? MESSAGE.M12a : MESSAGE.M12b;
                } else {
                  msg = onClosedResult;
                  props.setLoadingPromise(null);
                  props.confirmMessage('error', msg);
                  return;
                }
                props
                  .confirmFn('Confirmation', {
                    description: msg
                  })
                  .catch(function() {
                    props.onAccept();
                  })
                  .finally(function() {
                    props.setLoadingPromise(null);
                  });
              });
              props.setLoadingPromise(onclosedPromise);
            }
            return success;
          });
        } else {
          props.confirmMessage('error', result.message);
        }
      });
    } else {
      valManager.reset();
      props.onReject();
      props.onClosed();
    }
  }

  /**
   * Validate email format for to, cc email
   */
  function validateEmailFormat(props) {
    var validation = props.validation;
    var email = props.email;
    var messages = [];

    // Check to emails
    var invalidToEmails = checkEmail(email, 'to');

    // Check cc emails
    var invalidCcEmails = checkEmail(email, 'cc');

    // Add error messages
    !isEmpty(invalidToEmails) && messages.push(invalidToEmails);
    !isEmpty(invalidCcEmails) && messages.push(invalidCcEmails);

    // Update errors to validation manager
    !isEmpty(messages) && validation.updateErrors(messages);

    return isEmpty(messages);
  }

  /**
   * Check list email if invalid
   * Return error message of invalid email
   */
  function checkEmail(email, type) {
    var emails = split(';', email[type]);
    var invalidEmails = [];
    forEach(function(email) {
      if (email && !constants.EMAIL_VALIDATION.REGEX.test(email)) {
        invalidEmails.push(email);
      }
    }, emails);

    return !isEmpty(invalidEmails)
      ? {
          key: type,
          messages: [constants.EMAIL_VALIDATION.MESSAGE(invalidEmails)]
        }
      : null;
  }

  /**
   * Set to'semail Address
   * @param props
   * @param value
   */
  function setToAddress(props, value) {
    props.email.setDataByKey('to', composeEmailValue(value));
  }

  /**
   * Set Cc's email address
   * @param props
   * @param value
   */
  function setCcAddress(props, value) {
    props.email.setDataByKey('cc', composeEmailValue(value));
  }

  /**
   * Handle add new email
   * @param props
   * @param newData
   */
  function addNewEmail(props, newData) {
    return;
  }

  /**
   * Set email subject
   * @param props
   * @param value
   */
  function setSubject(props, value) {
    props.email.setDataByKey('subject', value.target.value);
  }

  /**
   * Set email delivery method
   * @param props
   * @param value
   */
  function setDeliveryMethod(props, emailBody, value) {
    props.email.setDeliveryMethod(emailBody, value);
  }

  /**
   * Set attachment type
   * @param props
   * @param value
   */
  function setAttachment(props, value) {
    props.email.setDataByKey('attachmentType', value.value);
  }

  /**
   * Set email body
   * @param props
   * @param value
   */
  function setEmailBody(props, value) {
    props.email.setDataByKey('body', value.target.value);
  }

  /**
   * Add rececipients email to from popup.
   * @param {Object} userAccountList
   */
  function addToEmailFromPopup(props, userAccountList) {
    addEmails(props.email, 'to', userAccountList);
  }

  /**
   * Add rececipients email cc from popup.
   * @param {Object} userAccountList
   */
  function addCcEmailFromPopup(props, userAccountList) {
    addEmails(props.email, 'cc', userAccountList);
  }

  /**
   * Add list email when select in popup
   * @param {Obejct} email
   * @param {string} label : to, cc
   * @param {Array} userAccountList
   */
  function addEmails(email, label, userAccountList) {
    var addEmails = pluck('emailAddress', userAccountList);
    var currentEmails = split(';', email[label]);
    var newEmails = chain(currentEmails)
      .concat(addEmails)
      .compact()
      .value();

    if (addEmails.length > 0) {
      email[label] = uniq(newEmails).join(';');
    }
  }

  /**
   * Compose to email value
   * @param {Array} emails
   */
  function composeEmailValue(emailValue) {
    var emails = chain(emailValue)
      .split(';')
      .compact()
      .value();

    return uniq(emails).join(';');
  }

  /**
   * Get email templates
   */
  function getEmailTemplates(props, type) {
    var fetcher = type === constants.EMAIL_TYPE.dashboard ? dashboardDataFetcher : reportsDataFetcher;
    return fetcher.fetchEmailTemplates(constants.TEMPLATE_TYPE);
  }

  module.exports = emailActions;
})();



// WEBPACK FOOTER //
// ./ui/share/components/controls/CommonPopup/Izenda.EmailActions.js